import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  EventEmitter,
  Output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { PageEvent } from '@angular/material/paginator';
import { PartnerUserModel, PartnerUsersResponseModel } from '@core/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TableComponent } from '@custom-components/general/table/table.component';
import { COMPANY_USERS_TABLE_COLUMNS_CONFIG } from '@core/constants/columns-config.const';
import { MY_COMPANY_USERS_PAGE_SIZE } from '@core/constants/my-company.constant';
import { FilledButtonComponent } from '@custom-components/general/button';

@Component({
  selector: 'xemex-my-company-users-tab',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './my-company-users-tab.component.html',
  styleUrl: './my-company-users-tab.component.scss',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    TranslateModule,
    TableComponent,
    FilledButtonComponent,
  ],
})
export class MyCompanyUsersTabComponent {
  users = input<PartnerUsersResponseModel>({ numberOfItems: 0, users: [] });
  permissions = input<string[]>([]);
  isLoading = input<boolean>(false);

  @Output() createUser = new EventEmitter<void>();
  @Output() editUser = new EventEmitter<PartnerUserModel>();
  @Output() deleteUser = new EventEmitter<string>();
  @Output() resendInvite = new EventEmitter<string>();
  @Output() userPageChange = new EventEmitter<{ pageIndex: number; pageSize: number }>();

  protected readonly columns = COMPANY_USERS_TABLE_COLUMNS_CONFIG;
  protected readonly pageSize = MY_COMPANY_USERS_PAGE_SIZE;

  protected readonly tableData = computed(
    () =>
      this.users().users.map(u => ({
        ...u,
        translatedRole: this.translate.instant('roles.roleNames.' + u.roleName.toLowerCase()),
        translatedStatus: this.translate.instant(
          u.status === 'Active'
            ? 'user.inviteAccepted'
            : u.status === 'Disabled'
              ? 'user.disabled'
              : 'user.invitePending',
        ),
      })) as unknown as Record<string, unknown>[],
  );

  protected hasAnyAction = computed(() =>
    this.permissions().some(p =>
      ['edit_user_details', 'delete_users', 'create_new_users'].includes(p),
    ),
  );

  constructor(private readonly translate: TranslateService) {}

  protected onPageChange(event: PageEvent): void {
    this.userPageChange.emit({ pageIndex: event.pageIndex, pageSize: event.pageSize });
  }

  onCreateUser() {
    this.createUser.emit();
  }
  onEditUser(userOid: string) {
    const user = this.tableData().find(u => u['objectId'] === userOid);
    if (user) this.editUser.emit(user as unknown as PartnerUserModel);
  }
  onDeleteUser(userOid: string) {
    this.deleteUser.emit(userOid);
  }
  onResendInvite(userOid: string) {
    this.resendInvite.emit(userOid);
  }
}
